// MMIGauge.js
// Semi-circle gauge showing the Market Mood Index (MMI) with zone legend.
import React from 'react';

const ZONE_CONFIG = {
    // Zone colours darkened for WCAG AA: white bar text needs >= 4.5:1 on each
    // (audit contrast batch: was #16a34a 3.3:1, #0d9488 3.7:1, #f97316 2.8:1, #dc2626 4.8:1)
    'Extreme Fear': { color: '#15803d', emoji: '😱', range: '< 30',   approx: 15, desc: 'Markets may be oversold — potential opportunity to open fresh positions.' },
    'Fear':         { color: '#0f766e', emoji: '😟', range: '30 – 50', approx: 40, desc: 'Caution advised — wait if fear is rising, consider entering if fear is reducing.' },
    'Greed':        { color: '#c2410c', emoji: '😏', range: '50 – 70', approx: 60, desc: 'Markets may be heating up — be cautious & patient before new entries.' },
    'Extreme Greed':{ color: '#b91c1c', emoji: '😈', range: '> 70',   approx: 85, desc: 'Markets likely overbought — avoid opening fresh positions.' },
};

// cx=110, cy=108, r=82 — semi-circle opens downward (flat edge at bottom)
const CX = 110, CY = 108, R = 82;

const trackPath = `M ${CX - R},${CY} A ${R},${R} 0 0,1 ${CX + R},${CY}`;

const buildFillPath = (pct) => {
    if (pct <= 0) return '';
    const clamped = Math.min(pct, 99.99);
    const theta   = (clamped / 100) * Math.PI;
    const x       = CX - R * Math.cos(theta);
    const y       = CY - R * Math.sin(theta);
    // Always 0 — every portion of a semicircle is ≤ 180°, so never the large arc
    return `M ${CX - R},${CY} A ${R},${R} 0 0,1 ${x.toFixed(2)},${y.toFixed(2)}`;
};

const MMIGauge = ({ mmi, type = 'weekly' }) => {
    if (!mmi) return null;

    // Daily wraps compare against yesterday's MMI, weekly wraps against last week's —
    // so the change strip / footnote must name the right period.
    const isWeekly    = type === 'weekly';
    const changeLabel = isWeekly ? 'Weekly Change' : 'Daily Change';

    const { value, zone, previousValue } = mmi;
    const cfg          = ZONE_CONFIG[zone] || ZONE_CONFIG['Fear'];
    const hasExactValue = value > 0;
    const clamped      = Math.max(0, Math.min(100, value));
    // When no exact value, use zone midpoint so the arc is always visible
    const displayValue = hasExactValue ? clamped : cfg.approx;
    const fillPath     = buildFillPath(displayValue);

    return (
        <div className="mmi-wrapper">
            <p className="wrap-section-title" style={{ marginBottom: '0.75rem' }}>
                Market Mood Index (MMI)
            </p>

            {/* ── Gauge ── */}
            <div className="mmi-gauge-container">
                <svg viewBox="0 0 220 115" className="mmi-svg" aria-label={`MMI ${hasExactValue ? value + '%' : zone} — ${zone}`}>
                    {/* Track */}
                    <path
                        d={trackPath}
                        fill="none"
                        stroke="#e5e7eb"
                        strokeWidth={18}
                        strokeLinecap="round"
                    />
                    {/* Coloured fill — always drawn (exact value or zone approx) */}
                    <path
                        d={fillPath}
                        fill="none"
                        stroke={cfg.color}
                        strokeWidth={18}
                        strokeLinecap="round"
                    />
                    {/* Centre value — numeric when available, zone name when not */}
                    {hasExactValue ? (
                        <text x={CX} y={CY - 22} textAnchor="middle" className="mmi-value-text" fill="#111827">
                            {value.toFixed(1)}%
                        </text>
                    ) : (
                        <text x={CX} y={CY - 22} textAnchor="middle" className="mmi-value-text" fill={cfg.color}>
                            {zone}
                        </text>
                    )}
                    {/* Zone label — emoji + name when exact, emoji only when zone-only */}
                    <text x={CX} y={CY - 2} textAnchor="middle" className="mmi-zone-text" fill={cfg.color}>
                        {hasExactValue ? `${zone} ${cfg.emoji}` : cfg.emoji}
                    </text>
                </svg>

                {/* Change strip — only when both current and previous values are known */}
                {hasExactValue && previousValue != null && previousValue > 0 && (
                    <div className="mmi-change-strip">
                        <span className="mmi-change-label">{changeLabel}</span>
                        <span className="mmi-change-values">
                            {Number(previousValue).toFixed(1)}%
                            <span className="mmi-arrow"> ⟶ </span>
                            {value.toFixed(1)}%
                        </span>
                    </div>
                )}
            </div>

            {/* ── Legend ── */}
            <div className="mmi-legend-wrapper">
                <p className="mmi-legend-title">How to understand this ?</p>
                <div className="mmi-legend-grid">
                    {Object.entries(ZONE_CONFIG).map(([zoneName, zCfg]) => (
                        <div
                            key={zoneName}
                            className={`mmi-legend-item${zone === zoneName ? ' mmi-legend-active' : ''}`}
                        >
                            <div className="mmi-legend-pill" style={{ background: zCfg.color }}>
                                {zCfg.range}
                            </div>
                            <p className="mmi-legend-zone">
                                {zoneName} {zCfg.emoji}
                            </p>
                            <p className="mmi-legend-desc">{zCfg.desc}</p>
                        </div>
                    ))}
                </div>
                <p className="mmi-legend-note">
                    <span>⏱</span>&nbsp;
                    <strong>{changeLabel}</strong> shows how investor sentiment is shifting — from fear to greed or vice versa.
                    Rising MMI suggests increasing optimism; falling MMI signals growing caution or risk-off mood.
                </p>
            </div>
        </div>
    );
};

export default MMIGauge;
